/**
 * AddBoardColumnModal Component
 * Modal for adding new board columns (status)
 * Handles column creation for kanban board
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography, Avatar, Divider, FormControl, InputLabel, Select, MenuItem, Tooltip } from '@mui/material';
import { ViewKanban, Assignment, Check } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchProjectDropdownsByUser } from '../../store';
import { COLORS, isValidHex } from '../../shared/colors';
import { BOARD_COLUMN_PRESET_COLORS as PRESET_COLORS } from '../../shared/appConstants';
import CustomColorPopover from '../shared/CustomColorPopover';

const AddBoardColumnModal = ({ open, onClose, onSubmit, selectedProjectId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectDropdownsByUser } = useSelector(state => state.tickets);
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState('');
  const [colour, setColour] = useState(PRESET_COLORS[0]);
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);

  useEffect(() => {
    if (open) {
      dispatch(fetchProjectDropdownsByUser());
      if (selectedProjectId) {
        setProjectId(selectedProjectId);
      }
    }
  }, [open, dispatch, selectedProjectId]);

  const previewColor = isValidHex(colour) ? colour : PRESET_COLORS[0];

  const handleCancel = () => {
    setName('');
    setProjectId('');
    setColour(PRESET_COLORS[0]);
    onClose && onClose();
  };

  const handleCreate = () => {
    if (name.trim() && projectId && isValidHex(colour)) {
      onSubmit && onSubmit({ name: name.trim(), projectId, colour });
      handleCancel();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: COLORS.brand.primaryDark }}>
            <ViewKanban />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight="600">{t('boardColumn.addNewColumn')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('boardColumn.createNewBoardColumn')}</Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <TextField
          fullWidth
          label={t('boardColumn.columnName')}
          value={name}
          onChange={(e) => {
            const value = e.target.value;
            if (value.length <= 25) {
              setName(value);
            }
          }}
          placeholder={t('boardColumn.enterColumnName')}
          required
          variant="outlined"
          inputProps={{ maxLength: 25 }}
          helperText={`${name.length}/25 characters`}
          sx={{ mb: 3 }}
        />

        {/* Color Picker */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 1 }}>
            Column Color
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
            {PRESET_COLORS.map((c) => (
              <Tooltip key={c} title={c}>
                <Box
                  onClick={() => setColour(c)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: c,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: colour === c ? `2px solid ${COLORS.gray[800]}` : '2px solid transparent',
                    transition: 'transform 0.15s',
                    '&:hover': { transform: 'scale(1.15)' }
                  }}
                >
                  {colour === c && <Check sx={{ fontSize: 14, color: '#fff' }} />}
                </Box>
              </Tooltip>
            ))}
            {/* Custom color picker */}
            <Tooltip title="Custom color">
              <Box
                onClick={(e) => setColorPickerAnchor(e.currentTarget)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: !PRESET_COLORS.includes(colour) && isValidHex(colour) ? `2px solid ${COLORS.gray[800]}` : `2px dashed ${COLORS.gray[400]}`,
                  background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                  transition: 'transform 0.15s',
                  overflow: 'hidden',
                  '&:hover': { transform: 'scale(1.15)' }
                }}
              >
                {!PRESET_COLORS.includes(colour) && isValidHex(colour) && <Check sx={{ fontSize: 14, color: '#fff' }} />}
              </Box>
            </Tooltip>

            <CustomColorPopover
              open={Boolean(colorPickerAnchor)}
              anchorEl={colorPickerAnchor}
              onClose={() => setColorPickerAnchor(null)}
              colour={colour}
              onChange={setColour}
            />
          </Box>
          {/* Preview */}
          <Box
            sx={{
              mt: 2.5,
              px: 2,
              py: 1,
              borderRadius: 2,
              bgcolor: previewColor + '29',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: previewColor }} />
            <Typography variant="caption" fontWeight={500} color="text.secondary">
              {name || 'Column preview'}
            </Typography>
          </Box>
        </Box>

        <FormControl fullWidth variant="outlined">
          <InputLabel>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Assignment sx={{ fontSize: 16, color: COLORS.secondary }} />
              {t('boardColumn.project')}
            </Box>
          </InputLabel>
          <Select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            label={t('boardColumn.project')}
            renderValue={(selected) => {
              if (!selected) return '';
              const project = projectDropdownsByUser.find(p => p.id === selected);
              if (!project) return '';
              return (
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ width: 20, height: 20, fontSize: '10px', bgcolor: COLORS.brand.primaryDark }}>
                    {project.name.charAt(0)}
                  </Avatar>
                  {project.name}
                </Box>
              );
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200,
                  overflow: 'auto'
                }
              }
            }}
          >

            {projectDropdownsByUser.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '12px', bgcolor: COLORS.brand.primaryDark }}>
                    {project.name.charAt(0)}
                  </Avatar>
                  {project.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCancel} variant="outlined">{t('common.cancel')}</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!name.trim() || !projectId || !isValidHex(colour)}
          sx={{
            bgcolor: COLORS.brand.primaryDark,
            color: COLORS.white,
            '&:hover': { bgcolor: COLORS.brand.primaryDarkHover, color: COLORS.white },
            '&.Mui-disabled': {
              bgcolor: COLORS.slate[200],
              color: COLORS.slate[400]
            }
          }}
        >
          {t('common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBoardColumnModal;

